<template>
  <div class="px-4 py-4 site-meta-page md:px-5">
    <div class="mx-auto space-y-5">
      <section class="p-5 bg-white border shadow-sm rounded-2xl border-slate-200">
        <div class="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p class="text-[15px] font-semibold text-slate-900">도메인 설정</p>
            <p class="mt-1 text-sm text-slate-500">사이트별 도메인과 HTML 메타 정보를 관리합니다. sitemap 은 도메인 기준으로 자동 생성됩니다.</p>
            <p class="mt-2 text-sm text-slate-600"><span class="font-bold text-red-500">*</span> 표시는 필수 입력 항목입니다.</p>
          </div>

          <div
            class="flex items-center gap-2 px-4 py-3 text-sm font-semibold border rounded-xl border-amber-200 bg-amber-50 text-amber-700"
          >
            <a :href="defaultPreviewUrl" target="_blank" class="underline underline-offset-4">{{ defaultPreviewUrl }}</a>
            <button type="button" class="copy-icon-btn" title="URL 복사" @click="page.copyDefaultPreviewUrl()">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                width="14"
                height="14"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M13.19 8.688a4.5 4.5 0 0 1 1.242 7.244l-4.5 4.5a4.5 4.5 0 0 1-6.364-6.364l1.757-1.757m13.35-.622 1.757-1.757a4.5 4.5 0 0 0-6.364-6.364l-4.5 4.5a4.5 4.5 0 0 0 1.242 7.244"
                />
              </svg>
            </button>
          </div>
        </div>
      </section>

      <section v-if="page.isLoading" class="p-5 bg-white border shadow-sm rounded-2xl border-slate-200">
        <p class="text-sm font-semibold text-slate-600">불러오는 중입니다.</p>
      </section>

      <section class="p-5 bg-white border shadow-sm rounded-2xl border-slate-200">
        <div class="pb-4 mb-4 border-b border-slate-200">
          <p class="text-base font-semibold text-slate-900">기본 정보</p>
          <p class="mt-1 text-sm text-slate-500">설정 대상 사이트 정보입니다.</p>
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          <div class="info-card">
            <span class="info-label">idxLeSiteMain</span>
            <p class="info-value">{{ page.form.idxLeSiteMain || "-" }}</p>
          </div>
          <div class="info-card">
            <span class="info-label">Company</span>
            <p class="info-value">{{ page.form.companyName || "-" }}</p>
          </div>
        </div>
      </section>

      <section class="p-5 bg-white border shadow-sm rounded-2xl border-slate-200">
        <div class="pb-4 mb-4 border-b border-slate-200">
          <p class="text-base font-semibold text-slate-900">Domain</p>
          <p class="mt-1 text-sm text-slate-500">일반 도메인과 www 도메인을 함께 관리합니다.</p>
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          <label class="form-field">
            <span class="form-label"><span class="text-red-500">*</span> 일반 도메인 (@)</span>
            <input
              :value="page.domainBase"
              @input="page.updateDomainBase($event.target.value)"
              type="text"
              class="form-control"
              placeholder="연결할 도메인을 입력해주세요. ex) carrotglobal.com"
            />
          </label>

          <label class="form-field">
            <span class="form-label"><span class="text-red-500">*</span> www 도메인</span>
            <div class="compound-control">
              <span class="compound-addon">www.</span>
              <input
                :value="page.domainBase"
                @input="page.updateDomainBase($event.target.value)"
                type="text"
                class="compound-input"
              />
            </div>
          </label>
        </div>
      </section>

      <section class="p-5 bg-white border shadow-sm rounded-2xl border-slate-200">
        <div class="pb-4 mb-4 border-b border-slate-200">
          <p class="text-base font-semibold text-slate-900">HTML</p>
          <p class="mt-1 text-sm text-slate-500">브라우저 탭과 검색 노출에 사용되는 메타 정보입니다.</p>
        </div>

        <div class="grid gap-4">
          <label class="form-field">
            <span class="form-label"><span class="text-red-500">*</span> html title</span>
            <input
              v-model="page.form.htmlTitle"
              type="text"
              class="form-control"
              placeholder="사이트의 탭 이름을 입력해주세요. ex) 평생교육 화상영어"
            />
          </label>

          <label class="form-field">
            <span class="form-label"><span class="text-red-500">*</span> html keywords</span>
            <input
              v-model="page.form.htmlKeywords"
              type="text"
              class="form-control"
              placeholder="검색 노출 키워드를 쉼표로 구분하여 입력해주세요. ex) 평생교육, 전화화상, 화상영어"
            />
          </label>

          <label class="form-field">
            <span class="form-label"><span class="text-red-500">*</span> html description</span>
            <textarea
              v-model="page.form.htmlDescription"
              rows="3"
              class="form-control"
              placeholder="검색 결과에 표시될 사이트 설명을 입력해주세요. ex) 울진군장학재단이 지원하는 화상영어, 원어민 1:1 맞춤 수업"
            ></textarea>
          </label>
        </div>
      </section>

      <section class="p-5 bg-white border shadow-sm rounded-2xl border-slate-200">
        <div class="pb-4 mb-4 border-b border-slate-200">
          <p class="text-base font-semibold text-slate-900">대표 이미지 설정</p>
          <p class="mt-1 text-sm text-slate-500">링크 공유 및 검색 노출에 사용되는 이미지를 설정합니다.</p>
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          <div class="upload-card">
            <div>
              <p class="text-sm font-bold text-slate-900">html property img</p>
              <p class="mt-1 text-sm text-slate-500">링크 공유 시 미리보기 이미지</p>
            </div>

            <label class="form-field">
              <span class="form-label"><span class="text-red-500">*</span> 파일 업로드</span>
              <input
                type="file"
                name="htmlPropertyImg"
                class="file-control"
                accept="image/*"
                @change="(event) => page.fileChange(event, 'htmlPropertyImg')"
              />
            </label>

            <input
              v-if="page.form.htmlPropertyImg"
              readonly
              type="text"
              :value="page.form.htmlPropertyImg"
              class="form-control link-control"
              @click="page.openFile(page.form.htmlPropertyImg)"
            />

            <div class="form-field">
              <span class="form-label">파일명</span>
              <div class="readonly-box min-h-[2.875rem]">{{ page.form.htmlPropertyImgName || "-" }}</div>
            </div>

            <button
              v-if="page.form.htmlPropertyImg"
              type="button"
              class="delete-button"
              @click="page.deleteImage('htmlPropertyImg', 'htmlPropertyImgName')"
            >
              Delete
            </button>
          </div>

          <div class="upload-card">
            <div>
              <p class="text-sm font-bold text-slate-900">html itemprop img</p>
              <p class="mt-1 text-sm text-slate-500">검색엔진/구조화 데이터용 이미지</p>
            </div>

            <label class="form-field">
              <span class="form-label"><span class="text-red-500">*</span> 파일 업로드</span>
              <input
                type="file"
                name="htmlItempropImg"
                class="file-control"
                accept="image/*"
                @change="(event) => page.fileChange(event, 'htmlItempropImg')"
              />
            </label>

            <input
              v-if="page.form.htmlItempropImg"
              readonly
              type="text"
              :value="page.form.htmlItempropImg"
              class="form-control link-control"
              @click="page.openFile(page.form.htmlItempropImg)"
            />

            <div class="form-field">
              <span class="form-label">파일명</span>
              <div class="readonly-box min-h-[2.875rem]">{{ page.form.htmlItempropImgName || "-" }}</div>
            </div>

            <button
              v-if="page.form.htmlItempropImg"
              type="button"
              class="delete-button"
              @click="page.deleteImage('htmlItempropImg', 'htmlItempropImgName')"
            >
              Delete
            </button>
          </div>
        </div>
      </section>

      <section class="p-5 bg-white border shadow-sm rounded-2xl border-slate-200">
        <div class="pb-4 mb-4 border-b border-slate-200">
          <p class="text-base font-semibold text-slate-900">파비콘 설정</p>
          <p class="mt-1 text-sm text-slate-500">브라우저 탭과 즐겨찾기에 표시되는 아이콘을 설정합니다. 비워두면 기본 아이콘이 사용됩니다.</p>
        </div>

        <div class="grid gap-4 md:grid-cols-2">
          <div class="upload-card">
            <div>
              <p class="text-sm font-bold text-slate-900">파비콘</p>
              <p class="mt-1 text-sm text-slate-500">정사각형 png 또는 ico 권장 (192 x 192 이상)</p>
            </div>

            <label class="form-field">
              <span class="form-label">파일 업로드</span>
              <input
                type="file"
                name="htmlIconImg"
                class="file-control"
                accept="image/png,image/x-icon,image/svg+xml"
                @change="(event) => page.fileChange(event, 'htmlIconImg')"
              />
            </label>

            <input
              v-if="page.form.htmlIconImg"
              readonly
              type="text"
              :value="page.form.htmlIconImg"
              class="form-control link-control"
              @click="page.openFile(page.form.htmlIconImg)"
            />

            <div class="form-field">
              <span class="form-label">파일명</span>
              <div class="readonly-box min-h-[2.875rem]">{{ page.form.htmlIconImgName || "-" }}</div>
            </div>

            <button
              v-if="page.form.htmlIconImg"
              type="button"
              class="delete-button"
              @click="page.deleteImage('htmlIconImg', 'htmlIconImgName')"
            >
              Delete
            </button>
          </div>
        </div>
      </section>
      <section
        v-if="page.form.createDate || page.form.updateDate"
        class="p-5 bg-white border shadow-sm rounded-2xl border-slate-200"
      >
        <div class="pb-4 mb-4 border-b border-slate-200">
          <p class="text-base font-semibold text-slate-900">이력</p>
        </div>
        <div class="grid gap-4 md:grid-cols-2">
          <div class="info-card">
            <span class="info-label">등록일</span>
            <p class="info-value">{{ page.form.createDate || "-" }}</p>
          </div>
          <div class="info-card">
            <span class="info-label">수정일</span>
            <p class="info-value">{{ page.form.updateDate || "-" }}</p>
          </div>
        </div>
      </section>

      <section class="p-5 bg-white border shadow-sm rounded-2xl border-slate-200">
        <div class="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
          <p class="text-sm text-slate-500">저장은 등록/수정을 구분하지 않고 동일하게 처리됩니다.</p>
          <div class="flex flex-col gap-3 sm:flex-row">
            <button type="button" class="secondary-button" @click="page.goBack">돌아가기</button>
            <button type="button" :disabled="page.isSaving" class="primary-button" @click="page.save">
              {{ page.hasExistingSeoData ? "수정" : "저장" }}
            </button>
          </div>
        </div>
      </section>
    </div>

    <Transition name="toast-fade">
      <div v-if="page.toastVisible" class="toast-notification">
        {{ page.toastMessage }}
      </div>
    </Transition>
  </div>
</template>

<script>
import { computed, onMounted, reactive } from "vue";
import { useRoute, useRouter } from "vue-router";
import axios from "@/plugins/axios.js";

const DEFAULT_FORM = () => ({
  idxLeSiteMainSeo: "",
  idxLeSiteMain: "",
  companyName: "",
  domain1: "",
  domain2: "",
  htmlTitle: "",
  htmlKeywords: "",
  htmlDescription: "",
  htmlPropertyImg: "",
  htmlPropertyImgName: "",
  htmlItempropImg: "",
  htmlItempropImgName: "",
  htmlIconImg: "",
  htmlIconImgName: "",
  locUrl1: "",
  locUrl2: "",
  createDate: "",
  updateDate: "",
});

export default {
  name: "SiteMetaConfig",
  setup() {
    const route = useRoute();
    const router = useRouter();

    const defaultPreviewUrl = computed(() => {
      const currentOrigin = window.location.origin;
      const isStage = currentOrigin.includes("stage") || currentOrigin.includes("localhost") || currentOrigin.includes("local");
      const idxLeSiteMain = page.form.idxLeSiteMain || route.query.idxLeSiteMain || "";

      return isStage
        ? "https://stage-lifeedu.carrotenglish.net/home?idxLeSiteMain=" + idxLeSiteMain
        : "https://lifeedu.carrotapply.com/home?idxLeSiteMain=" + idxLeSiteMain;
    });

    const page = reactive({
      form: DEFAULT_FORM(),
      domainBase: "",
      isLoading: false,
      isSaving: false,
      hasExistingSeoData: false,
      toastMessage: "",
      toastVisible: false,
      toastTimer: null,

      showToast(message) {
        page.toastMessage = message;
        page.toastVisible = true;

        if (page.toastTimer) {
          clearTimeout(page.toastTimer);
        }

        page.toastTimer = setTimeout(() => {
          page.toastVisible = false;
        }, 2000);
      },

      async copyDefaultPreviewUrl() {
        try {
          await navigator.clipboard.writeText(defaultPreviewUrl.value);
          page.showToast("URL이 복사되었습니다.");
        } catch (error) {
          console.error("URL 복사 중 오류가 발생했습니다:", error);
          page.showToast("URL 복사에 실패했습니다.");
        }
      },

      // 저장값이 canonical·sitemap·robots.txt 의 호스트로 그대로 쓰이므로 호스트만 남기고 소문자로 통일한다.
      // 전체 URL 을 붙여넣는 경우가 있어 경로·쿼리·포트까지 제거한다.
      stripDomainPrefix(value) {
        if (!value) {
          return "";
        }

        return String(value)
          .trim()
          .replace(/^https?:\/\//i, "")
          .replace(/^www\./i, "")
          .replace(/[/?#].*$/, "")
          .replace(/:\d+$/, "")
          .toLowerCase();
      },

      setDomainFields(baseValue) {
        const normalizedBase = page.stripDomainPrefix(baseValue);
        page.domainBase = normalizedBase;
        page.form.domain1 = normalizedBase;
        page.form.domain2 = normalizedBase ? `www.${normalizedBase}` : "";
        page.setSitemapFields(normalizedBase);
      },

      updateDomainBase(value) {
        page.setDomainFields(value);
      },

      // sitemap URL 은 백엔드가 domain1/domain2 로 생성한다. 여기서는 도메인 기준으로 값을 맞춰 저장만 한다.
      setSitemapFields(normalizedBase) {
        page.form.locUrl1 = normalizedBase ? `https://${normalizedBase}/sitemap.xml` : "";
        page.form.locUrl2 = normalizedBase ? `https://www.${normalizedBase}/sitemap.xml` : "";
      },

      normalizeForm(data = {}) {
        const normalizedBase = page.stripDomainPrefix(data.domain1 || data.domain2 || "");

        page.form = {
          ...DEFAULT_FORM(),
          idxLeSiteMainSeo: String(data.idxLeSiteMainSeo || ""),
          idxLeSiteMain: String(data.idxLeSiteMain || route.query.idxLeSiteMain || ""),
          companyName: data.companyName || data.kname || data.siteName || route.query.companyName || "",
          domain1: normalizedBase,
          domain2: normalizedBase ? `www.${normalizedBase}` : "",
          htmlTitle: data.htmlTitle || "",
          htmlKeywords: data.htmlKeywords || "",
          htmlDescription: data.htmlDescription || "",
          htmlPropertyImg: data.htmlPropertyImg || "",
          htmlPropertyImgName: data.htmlPropertyImgName || "",
          htmlItempropImg: data.htmlItempropImg || "",
          htmlItempropImgName: data.htmlItempropImgName || "",
          htmlIconImg: data.htmlIconImg || "",
          htmlIconImgName: data.htmlIconImgName || "",
          locUrl1: normalizedBase ? `https://${normalizedBase}/sitemap.xml` : "",
          locUrl2: normalizedBase ? `https://www.${normalizedBase}/sitemap.xml` : "",
          createDate: data.createDate || "",
          updateDate: data.updateDate || "",
        };

        page.domainBase = normalizedBase;
      },

      async fileChange(event, fieldName) {
        const inputField = event.target;
        const file = inputField.files && inputField.files[0] ? inputField.files[0] : null;

        if (!file) {
          return;
        }

        try {
          const res = await page.uploadFile(file);
          const uploadUrl = res.data.data.uploadUrl;
          page.form[fieldName] = uploadUrl;
          page.form[`${fieldName}Name`] = page.setImageFileName(uploadUrl);
        } catch (error) {
          console.error("파일 업로드 중 오류가 발생했습니다:", error);
          alert("파일 업로드 중 오류가 발생했습니다.");
          inputField.value = "";
        }
      },

      async uploadFile(file) {
        const formData = new FormData();
        formData.append("file", file);

        return axios.post("/admin/upload", formData, {
          headers: {
            "Content-Type": "multipart/form-data",
          },
        });
      },

      deleteImage(fieldName, fileNameField) {
        page.form[fieldName] = "";
        page.form[fileNameField] = "";
        const inputField = document.querySelector(`input[name="${fieldName}"]`);
        if (inputField) {
          inputField.value = "";
        }
      },

      setImageFileName(url) {
        if (!url) {
          return "";
        }

        const parts = url.split("/");
        return parts[parts.length - 1];
      },

      openFile(url) {
        if (!url) {
          return;
        }

        window.open(url, "_blank");
      },

      validateForm() {
        const requiredFields = [
          { key: "domain1", label: "일반 도메인 (@)" },
          { key: "domain2", label: "www 도메인" },
          { key: "htmlTitle", label: "html title" },
          { key: "htmlKeywords", label: "html keywords" },
          { key: "htmlDescription", label: "html description" },
          { key: "htmlPropertyImg", label: "html property img" },
          { key: "htmlPropertyImgName", label: "html property img 파일명" },
          { key: "htmlItempropImg", label: "html itemprop img" },
          { key: "htmlItempropImgName", label: "html itemprop img 파일명" },
        ];

        const invalidField = requiredFields.find(({ key }) => {
          const value = page.form[key];
          return value === null || value === undefined || String(value).trim() === "";
        });

        if (invalidField) {
          alert(`${invalidField.label} 값을 입력해주세요.`);
          return false;
        }

        return true;
      },

      async load() {
        const idxLeSiteMain = route.query.idxLeSiteMain;
        if (!idxLeSiteMain) {
          alert("잘못된 접근입니다.");
          window.history.back();
          return;
        }

        page.form.idxLeSiteMain = String(idxLeSiteMain);
        page.isLoading = true;

        try {
          const res = await axios.get("/admin/siteMainSeo", {
            params: {
              idxLeSiteMain,
            },
          });

          if (res.data.success && res.data.data && typeof res.data.data === "object") {
            page.hasExistingSeoData = true;
            page.normalizeForm(res.data.data);
          } else {
            page.hasExistingSeoData = false;
            page.normalizeForm({
              idxLeSiteMain,
            });
          }
        } catch (error) {
          console.error("사이트 도메인 설정 조회 중 오류가 발생했습니다:", error);
        } finally {
          page.isLoading = false;
        }
      },

      makePayload() {
        return {
          idxLeSiteMain: Number(page.form.idxLeSiteMain),
          domain1: page.form.domain1,
          domain2: page.form.domain2,
          htmlTitle: page.form.htmlTitle,
          htmlKeywords: page.form.htmlKeywords,
          htmlDescription: page.form.htmlDescription,
          htmlPropertyImg: page.form.htmlPropertyImg,
          htmlPropertyImgName: page.form.htmlPropertyImgName,
          htmlItempropImg: page.form.htmlItempropImg,
          htmlItempropImgName: page.form.htmlItempropImgName,
          htmlIconImg: page.form.htmlIconImg,
          htmlIconImgName: page.form.htmlIconImgName,
          locUrl1: page.form.locUrl1,
          locUrl2: page.form.locUrl2,
        };
      },

      async save() {
        if (!page.form.idxLeSiteMain) {
          alert("idxLeSiteMain 정보가 없습니다.");
          return;
        }

        if (!page.validateForm()) {
          return;
        }

        page.isSaving = true;
        try {
          const res = await axios.post("/admin/siteMainSeo", page.makePayload());

          if (!res.data.success) {
            alert(res.data.message || "저장에 실패했습니다.");
            return;
          }

          if (res.data.data && typeof res.data.data === "object") {
            page.normalizeForm(res.data.data);
          }

          alert(page.hasExistingSeoData ? "도메인 수정이 완료되었습니다." : "도메인 설정이 완료되었습니다.");
          page.hasExistingSeoData = true;
          router.push({ name: "home" });
        } catch (error) {
          console.error("사이트 도메인 설정 저장 중 오류가 발생했습니다:", error);
          alert("저장 중 오류가 발생했습니다.");
        } finally {
          page.isSaving = false;
        }
      },

      goBack() {
        window.history.back();
      },
    });

    onMounted(() => {
      page.load();
    });

    return {
      page,
      defaultPreviewUrl,
    };
  },
};
</script>

<style scoped>
.site-meta-page {
  min-height: 100%;
  background: radial-gradient(circle at top left, rgba(226, 232, 240, 0.65), transparent 32rem),
    linear-gradient(180deg, #f8fafc 0%, #ffffff 42%);
}

.info-card,
.upload-card {
  border: 1px solid #e2e8f0;
  border-radius: 1rem;
  background: #f8fafc;
}

.info-card {
  padding: 1rem;
}

.upload-card {
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 1rem;
}

.info-label,
.form-label {
  font-size: 0.75rem;
  font-weight: 700;
  color: #64748b;
}

.info-value {
  margin-top: 0.5rem;
  word-break: break-all;
  font-size: 1.05rem;
  font-weight: 700;
  color: #0f172a;
}

.form-field {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.form-control,
.compound-control,
.file-control,
.readonly-box {
  width: 100%;
  border: 1px solid #cbd5e1;
  border-radius: 0.75rem;
  background-color: #fff;
  font-size: 0.95rem;
  color: #0f172a;
  outline: none;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease;
}

.form-control,
.file-control,
.readonly-box {
  min-height: 2.875rem;
  padding: 0.7rem 0.9rem;
}

.form-control:focus,
.compound-control:focus-within,
.file-control:focus {
  border-color: #94a3b8;
  box-shadow: 0 0 0 3px rgba(148, 163, 184, 0.15);
}

.compound-control {
  display: flex;
  min-height: 2.875rem;
  overflow: hidden;
}

.compound-addon {
  display: inline-flex;
  flex-shrink: 0;
  align-items: center;
  border-right: 1px solid #e2e8f0;
  background: #f8fafc;
  padding: 0 0.9rem;
  font-size: 0.9rem;
  font-weight: 700;
  color: #64748b;
}

.compound-addon-right {
  border-right: 0;
  border-left: 1px solid #e2e8f0;
}

.compound-input {
  min-width: 0;
  flex: 1;
  border: 0;
  background: transparent;
  padding: 0.7rem 0.9rem;
  color: #0f172a;
  outline: none;
}

.readonly-box {
  line-height: 1.6;
  word-break: keep-all;
  color: #334155;
}

.link-control {
  cursor: pointer;
  color: #2563eb;
}

.primary-button,
.secondary-button,
.delete-button {
  display: inline-flex;
  min-height: 2.875rem;
  align-items: center;
  justify-content: center;
  border-radius: 0.75rem;
  padding: 0.7rem 1.1rem;
  font-size: 0.95rem;
  font-weight: 700;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease,
    color 0.2s ease;
}

.primary-button {
  background: #2563eb;
  color: #fff;
}

.primary-button:hover {
  background: #1d4ed8;
}

.primary-button:disabled {
  cursor: not-allowed;
  background: #94a3b8;
}

.secondary-button {
  border: 1px solid #cbd5e1;
  background: #f8fafc;
  color: #475569;
}

.secondary-button:hover {
  background: #f1f5f9;
}

.delete-button {
  align-self: flex-start;
  border: 1px solid #fecaca;
  background: #fff;
  color: #dc2626;
}

.delete-button:hover {
  background: #fef2f2;
}

@media (max-width: 640px) {
  .compound-control {
    flex-wrap: wrap;
  }

  .compound-addon,
  .compound-addon-right {
    min-height: 2.5rem;
  }
}

.copy-icon-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  width: 1.6rem;
  height: 1.6rem;
  border: 1px solid #fcd34d;
  border-radius: 0.5rem;
  background: #fff;
  color: #b45309;
  transition:
    background-color 0.2s ease,
    border-color 0.2s ease;
}

.copy-icon-btn:hover {
  background: #fffbeb;
  border-color: #d97706;
}

.toast-notification {
  position: fixed;
  bottom: 2.5rem;
  left: 50%;
  transform: translateX(-50%);
  z-index: 100;
  padding: 0.75rem 1.25rem;
  border-radius: 0.75rem;
  background: rgba(15, 23, 42, 0.92);
  color: #fff;
  font-size: 0.9rem;
  font-weight: 600;
  box-shadow: 0 10px 25px rgba(15, 23, 42, 0.25);
}

.toast-fade-enter-active,
.toast-fade-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.toast-fade-enter-from,
.toast-fade-leave-to {
  opacity: 0;
  transform: translate(-50%, 0.5rem);
}
</style>
